/**
 * 가입 신청 — 한 화면에서 이메일·휴대폰을 **inline 으로** 인증하고 제출한다. 뒤에는 완료 화면만 있다.
 *
 * ★ 남은 시간은 **받은 즉시 한 번만** 절대시각으로 바꾼다. 서버가 절대시각을 보내지 않는 이유(클라이언트 시계)를
 *   여기서 되돌리지 않기 위해서다.
 * ★ 인증 완료는 **"그때 인증한 값"으로** 들고 있는다 — 주소·번호를 고치면 자동으로 무효가 된다.
 *   서버도 같은 대조를 하므로 이건 편의이지 보안 장치가 아니다.
 * ★ 이미 물어본 (대상, 코드) 쌍은 다시 묻지 않는다 — 확인 요청도 상한을 쓴다.
 */
import { signup } from '@geo-admin/shared/contract/api/index.ts';
import { SignupBody } from '@geo-admin/shared/contract/api/signup.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthCard, AuthShell, authField, Em, HeroPanel, JourneySteps, StatusCircle } from '../components/AuthShell.tsx';
import { CheckIcon } from '../components/icons.tsx';
import { emptyLinks, LinksField, type LinksValue, linksPayload, linksProblem } from '../components/LinksField.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { Button } from '../components/ui/button.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { ApiError, call } from '../lib/call.ts';
import { applyPhoneInput, formatPhone, phoneDigits } from '../lib/phone.ts';

/**
 * 가입은 하나다 — 계정과 첫 브랜드를 만들고 그 계정이 소유자다 (261013-account-brand-membership — 대행사 가입을 없앴다).
 * 브랜드를 여럿 관리하면 가입 뒤 「브랜드」에서 더한다
 */
export type SignupKind = 'brand';

// 계약 본문은 링크 「하나 이상」 refine 이 걸려 omit 할 수 없다 — 원본 객체를 쓴다
const Body = SignupBody;

/**
 * 폼은 계약 본문을 그대로 쓰고, 화면에만 있는 항목(비밀번호 확인)을 얹는다.
 * 희망 질문·추가 말씀은 **화면에서만** 뺐다(260917-signup-form-trim) — 계약·서버·DB 는 그대로라 제출은 빈 값을 싣는다.
 * 되살리려면 `git show d6a391d:admin/web/src/routes/signup.tsx` 의 "원하시는 질문" 절(질문 칸·예시·parseQueries)을 가져온다.
 */
// 링크(홈페이지·플레이스·그 밖)는 폼 바깥의 `LinksField` 상태로 받는다 — 칸 수가 바뀌는 목록이라서다
// 원하는 질문·브랜드명 쌍은 폼 바깥 상태로 받는다
const Form = Body.omit({
  desiredQuery: true,
  brandName: true,
  inquiry: true,
  homepageUrl: true,
  naverPlaceUrl: true,
  links: true,
})
  .extend({
    confirm: z.string(),
    // 계약은 옛 탭 호환으로 기본값(false)을 두지만, 폼은 늘 값이 있다 — 기본 해제
    agreeMarketing: z.boolean(),
  })
  .refine((v) => v.confirm === v.password, { message: '두 입력이 다릅니다', path: ['confirm'] });
type FormValues = z.infer<typeof Form>;

/** 동의 항목 — 전체 동의가 이 셋을 함께 켜고 끈다 */
const CONSENTS = ['agreeTerms', 'agreePrivacy', 'agreeMarketing'] as const;

export type Kind = 'phone' | 'email';
/** 인증 여부를 견주는 기준 — 표기만 바꿔 인증이 풀리거나 남지 않게 */
export const normalizeFor = (kind: Kind, v: string) =>
  kind === 'phone' ? v.replace(/\D/g, '') : v.trim().toLowerCase();

// ── 임시 저장 ───────────────────────────────────────────────────────────
// 코드를 보러 메일·문자 앱으로 갔다 오면 탭이 버려질 수 있다. 비밀번호·코드·인증 상태·동의는 저장하지 않는다.
const draftKey = (_kind: SignupKind) => 'livegpt.signup.draft';
type Draft = Pick<FormValues, 'email' | 'companyName' | 'name' | 'phone'>;
/**
 * 모양이 바뀌면 올린다 — 모르는 판은 버린다 (2: 여러 줄 질문 → 질문 칸 · 3: 질문·말씀 칸 제거).
 * ★ 폼 스키마가 strict 라 옛 판의 필드가 기본값에 섞이면 제출이 "모르는 키"로 막힌다
 */
const DRAFT_VERSION = 3;

function loadDraft(kind: SignupKind): Partial<Draft> {
  try {
    const raw = sessionStorage.getItem(draftKey(kind));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { v?: number } & Partial<Draft>;
    if (parsed.v !== DRAFT_VERSION) return {}; // 모르는 판은 버린다
    const { v: _v, ...rest } = parsed;
    return rest;
  } catch {
    return {}; // 프라이빗 창에서는 접근 자체가 던진다
  }
}

function saveDraft(kind: SignupKind, d: Draft): void {
  try {
    sessionStorage.setItem(draftKey(kind), JSON.stringify({ v: DRAFT_VERSION, ...d }));
  } catch {
    /* 저장이 안 돼도 가입은 계속된다 */
  }
}

function clearDraft(kind: SignupKind): void {
  try {
    sessionStorage.removeItem(draftKey(kind));
  } catch {
    /* 무시 */
  }
}

export function SignupPage({ kind = 'brand' }: { kind?: SignupKind }) {
  const [done, setDone] = useState(false);
  // 타이머는 페이지에 하나만 둔다 — 인증 칸마다 두면 리렌더가 배로 는다
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <AuthShell
      width="xl"
      aside={
        <HeroPanel
          eyebrow="가입 신청"
          title={
            <>
              <Em>AI 답변</Em>에 우리 업체가 나오도록 함께 준비합니다
            </>
          }
          lead="신청서를 남겨 주시면 담당자가 검토한 뒤 연락드립니다. 승인과 협의를 거쳐 리포트가 열립니다. 브랜드가 여럿이면 가입 뒤 「브랜드」에서 더할 수 있습니다."
        >
          {/* 가입 절차는 폼보다 먼저 보인다 — 좁은 화면에서도 히어로가 폼 위에 온다 (260916-client-self-signup) */}
          <JourneySteps current={done ? 1 : 0} />
        </HeroPanel>
      }
    >
      {done ? <DoneStep /> : <SignupForm kind={kind} now={now} onDone={() => setDone(true)} />}
      <SupportContact />
    </AuthShell>
  );
}

/** 폼의 묶음 제목 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-4">
      <legend className="mb-3 flex w-full items-center gap-3 font-bold text-slate-900 text-sm">
        {title}
        <span aria-hidden className="h-px flex-1 bg-slate-100" />
      </legend>
      {children}
    </fieldset>
  );
}

const hint = 'font-normal text-slate-500 text-xs';

function SignupForm({ kind, now, onDone }: { kind: SignupKind; now: number; onDone: () => void }) {
  const draft = useRef(loadDraft(kind)).current;
  const form = useForm<FormValues>({
    resolver: zodResolver(Form),
    defaultValues: {
      ...draft,
      agreeMarketing: false,
      // 대시를 붙이기 전의 임시 저장본도 같은 모양으로 보인다
      phone: draft.phone ? formatPhone(phoneDigits(draft.phone)) : '',
    },
  });
  /** 인증이 끝난 값 자체를 들고 있는다 — 입력이 바뀌면 자동으로 무효가 된다 */
  const [verified, setVerified] = useState<{ phone: string | null; email: string | null }>({
    phone: null,
    email: null,
  });
  // 원본 자산 링크 — 하나 이상 필수 (261010-agency-console)
  const [links, setLinks] = useState<LinksValue>(emptyLinks);
  const [linksTried, setLinksTried] = useState(false);
  // 원하는 질문 1개 + 브랜드명 1개 — 둘 다 필수 (운영자 결정 2026-10-11).
  // 브랜드명은 브랜드의 표시 이름이 되고, 업체명(위 「업체 정보」)은 계정 회사명이 된다 (261013-signup-company-name-to-account)
  const [desiredQuery, setDesiredQuery] = useState('');
  const [brandName, setBrandName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /**
   * 두 단계 — ① 계정 정보·약관 → ② 브랜드 등록. **제출은 ②에서 한 번**이다(서버 계약은 그대로). ①의 입력은 폼 상태에 남아
   * 「이전」으로 돌아가도 그대로다. 인증 유효창(30분) 안에 ②를 마쳐야 한다 — 서버가 제출 때 인증을 소비한다
   */
  const [step, setStep] = useState<'account' | 'brand'>('account');
  const e = form.formState.errors;

  const email = form.watch('email') ?? '';
  const phone = form.watch('phone') ?? '';
  const phoneField = form.register('phone');
  const isVerified = (kind: Kind, value: string) =>
    verified[kind] !== null && verified[kind] === normalizeFor(kind, value);

  // 임시 저장 — 비밀번호·코드·인증 상태·동의는 담지 않는다
  useEffect(() => {
    const sub = form.watch((v) =>
      saveDraft(kind, {
        email: v.email ?? '',
        companyName: v.companyName ?? '',
        name: v.name ?? '',
        phone: v.phone ?? '',
      }),
    );
    return () => sub.unsubscribe();
  }, [form, kind]);

  /** ① → ② — 계정 칸·필수 약관을 스키마로 검사하고 넘어간다 */
  const next = form.handleSubmit(() => {
    setError(null);
    setStep('brand');
    window.scrollTo({ top: 0 });
  });

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const values = form.getValues();
    setError(null);
    if (kind === 'brand') {
      setLinksTried(true);
      const problem =
        linksProblem(links) ??
        (desiredQuery.trim().length < 2 ? '원하는 질문을 넣어 주세요' : null) ??
        (!brandName.trim() ? '브랜드명을 넣어 주세요' : null);
      if (problem) {
        setError(problem);
        return;
      }
    }
    setSubmitting(true);
    try {
      const common = {
        email: values.email,
        password: values.password,
        name: values.name,
        phone: values.phone,
        agreeTerms: true as const,
        agreePrivacy: true as const,
        agreeMarketing: values.agreeMarketing === true,
      };
      await call(signup.submitSignup, {
        // 화면에서 뺀 항목 — 계약은 그대로 받는다(빈 배열·빈 문자열 허용)
        body: {
          ...common,
          companyName: values.companyName,
          desiredQuery: desiredQuery.trim(),
          brandName: brandName.trim(),
          inquiry: '',
          ...linksPayload(links),
        },
      });
      clearDraft(kind);
      onDone();
    } catch (err) {
      setError((err as Error).message);
      // 그 주소는 못 쓴다 — 이메일 인증만 풀고 휴대폰 인증은 남긴다
      if (err instanceof ApiError && err.code === 'email-taken') {
        setVerified((v) => ({ ...v, email: null }));
        setStep('account');
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * ★ **표시와 제출 게이트를 가른다** (nmstory D188 과 같은 규약). 전체 동의는 선택 항목(마케팅)까지 켜지만,
   *   제출은 필수 항목만 막는다(Form 스키마의 agreeTerms·agreePrivacy). 둘을 한 값으로 쓰면 마케팅 미동의가 가입을 막는다 —
   *   선택 동의 거부를 이유로 서비스를 거부하는 것은 개인정보보호법 제22조 제5항이 금지한다.
   */
  const agreeAllId = useId();
  const consents = form.watch(CONSENTS);
  const allChecked = consents.every(Boolean);
  const someChecked = consents.some(Boolean);
  const setAll = (next: boolean) => {
    for (const name of CONSENTS) {
      // 제출을 한 번 시도한 뒤라면 "동의하셔야" 오류를 곧바로 지운다
      form.setValue(name, next, { shouldDirty: true, shouldValidate: form.formState.isSubmitted });
    }
  };

  const ready = isVerified('email', email) && isVerified('phone', phone);
  return (
    <AuthCard
      title={
        // 단계는 흐리게 오른쪽에 — 제목은 「가입 신청」 하나다
        <span className="flex items-baseline justify-between gap-3">
          <span>가입 신청</span>
          <span className="whitespace-nowrap font-medium text-base text-slate-400">
            {step === 'account' ? '1/2' : '2/2'}
          </span>
        </span>
      }
      description={
        step === 'account'
          ? undefined
          : 'AI 답변에 나와야 할 브랜드를 등록합니다. 신청 내용을 확인하고 관리자가 직접 연락드립니다.'
      }
    >
      {step === 'account' ? (
        <form className="grid gap-7" onSubmit={next}>
          <Section title="계정 정보">
            <div>
              <Label>
                이메일
                <Input type="email" autoComplete="username" className={authField} {...form.register('email')} />
                <span className={hint}>로그인에 쓰는 주소입니다</span>
              </Label>
              <FieldError>{e.email?.message}</FieldError>
              <InlineVerify
                kind="email"
                target={email}
                now={now}
                verified={isVerified('email', email)}
                onVerified={(v) => setVerified((s) => ({ ...s, email: v }))}
              />
            </div>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <Label>
                비밀번호
                <Input
                  type="password"
                  autoComplete="new-password"
                  className={authField}
                  {...form.register('password')}
                />
                <span className={hint}>8자 이상</span>
                <FieldError>{e.password?.message}</FieldError>
              </Label>
              <Label>
                비밀번호 확인
                <Input
                  type="password"
                  autoComplete="new-password"
                  className={authField}
                  {...form.register('confirm')}
                />
                <FieldError>{e.confirm?.message}</FieldError>
              </Label>
            </div>
            <div className="grid items-start gap-4 sm:grid-cols-2">
              <Label>
                업체명
                <Input autoComplete="organization" className={authField} {...form.register('companyName')} />
                <FieldError>{e.companyName?.message}</FieldError>
              </Label>
              <Label>
                대표자명
                <Input autoComplete="name" className={authField} {...form.register('name')} />
                <FieldError>{e.name?.message}</FieldError>
              </Label>
            </div>
            <div>
              <Label>
                휴대폰 번호
                <Input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="010-1234-5678"
                  className={authField}
                  {...phoneField}
                  // 숫자만 받고 대시는 화면이 붙인다 — 인증 대조·서버는 숫자만 본다(lib/phone.ts).
                  // maxLength 를 두지 않는다: "+82 10-…" 붙여넣기가 형식을 맞추기 전에 잘린다(자릿수는 11 로 포맷이 자른다)
                  onChange={(ev) => {
                    const input = ev.target;
                    const next = applyPhoneInput(
                      form.getValues('phone') ?? '',
                      input.value,
                      input.selectionStart ?? input.value.length,
                      (ev.nativeEvent as InputEvent).inputType === 'deleteContentBackward',
                    );
                    input.value = next.value;
                    input.setSelectionRange(next.caret, next.caret);
                    void phoneField.onChange(ev);
                  }}
                />
                <span className={hint}>보고서, 공지사항 등 알림을 받습니다</span>
              </Label>
              <FieldError>{e.phone?.message}</FieldError>
              <InlineVerify
                kind="phone"
                target={phone}
                now={now}
                verified={isVerified('phone', phone)}
                onVerified={(v) => setVerified((s) => ({ ...s, phone: v }))}
              />
            </div>
          </Section>

          {/*
          ★ 개별 항목은 **접지 않는다** — 전체 동의 하나만 남으면 무엇에 동의했는지가 형식만 남는다(nmstory D188).
          ★ 선택 동의는 기본 해제이고 임시 저장에도 담지 않는다(260917-signup-marketing-consent).
        */}
          <fieldset className="overflow-hidden rounded-xl text-sm ring-1 ring-slate-200">
            <legend className="sr-only">약관 동의</legend>
            <label
              htmlFor={agreeAllId}
              className="flex cursor-pointer items-start gap-2.5 border-slate-200 border-b bg-live/5 p-4"
            >
              <IndeterminateCheckbox
                id={agreeAllId}
                checked={allChecked}
                indeterminate={someChecked && !allChecked}
                onChange={setAll}
                className="mt-0.5 h-4 w-4 accent-live"
              />
              <span className="font-bold text-slate-900">약관에 모두 동의합니다</span>
            </label>
            <div className="grid gap-2 bg-slate-50 p-4">
              <label className="flex cursor-pointer items-start gap-2.5">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-live" {...form.register('agreeTerms')} />
                <span>
                  (필수){' '}
                  <Link to="/terms" target="_blank" className="font-semibold text-slate-900 underline">
                    서비스 이용약관
                  </Link>
                  에 동의합니다
                </span>
              </label>
              <FieldError>{e.agreeTerms ? '이용약관에 동의하셔야 가입할 수 있습니다' : null}</FieldError>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-live" {...form.register('agreePrivacy')} />
                <span>
                  (필수){' '}
                  <Link to="/privacy" target="_blank" className="font-semibold text-slate-900 underline">
                    개인정보 수집·이용
                  </Link>
                  에 동의합니다
                </span>
              </label>
              <FieldError>{e.agreePrivacy ? '개인정보 수집·이용에 동의하셔야 가입할 수 있습니다' : null}</FieldError>
              <label className="flex cursor-pointer items-start gap-2.5">
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-live" {...form.register('agreeMarketing')} />
                <span>
                  (선택){' '}
                  <Link to="/marketing" target="_blank" className="font-semibold text-slate-900 underline">
                    마케팅 정보 수신
                  </Link>
                  에 동의합니다 <span className="text-slate-500 text-xs">(이메일·문자·카카오톡)</span>
                </span>
              </label>
            </div>
          </fieldset>

          <div className="grid gap-3">
            <FieldError>{error}</FieldError>
            <Button
              type="submit"
              variant="live"
              size="lg"
              // 비활성 문구가 좁은 화면에서 두 줄이 된다 — 글자 중간이 아니라 어절에서 끊는다
              className="h-auto min-h-12 w-full break-keep py-3 leading-snug"
              disabled={!ready}
            >
              {ready ? '다음 — 브랜드 등록' : '이메일·휴대폰 인증 후 다음으로 갈 수 있습니다'}
            </Button>
            <p className="text-center text-slate-500 text-sm">
              이미 계정이 있으신가요?{' '}
              <Link to="/login" className="font-semibold text-live hover:underline">
                로그인
              </Link>
            </p>
          </div>
        </form>
      ) : (
        <form className="grid gap-7" onSubmit={(ev) => void submit(ev)}>
          {/* 브랜드 — 이름(표시 이름)·홈페이지·링크·원하는 질문. 업체명은 위 계정 정보에 남는다 (261013-signup-company-name-to-account) */}
          <Section title="브랜드 정보">
            <Label>
              <span>
                브랜드명 <span className="text-bad">*</span>{' '}
                <span className={hint}>AI 답변에 노출되기 원하는 브랜드의 이름</span>
              </span>
              <Input
                className={authField}
                placeholder="예: 하남본치과"
                value={brandName}
                onChange={(ev) => setBrandName(ev.target.value)}
                maxLength={120}
              />
            </Label>
            <LinksField value={links} onChange={setLinks} showProblem={linksTried} />
            <Label>
              <span>
                원하는 질문 <span className="text-bad">*</span>{' '}
                <span className={hint}>
                  AI 에 이 질문을 물었을 때 답변에 위 브랜드가 나와야 합니다. 운영팀이 확인해 확정합니다
                </span>
              </span>
              <Input
                className={authField}
                placeholder="예: 강남 임플란트 치과 추천"
                value={desiredQuery}
                onChange={(ev) => setDesiredQuery(ev.target.value)}
                maxLength={300}
              />
            </Label>
          </Section>
          <div className="grid gap-3">
            <FieldError>{error}</FieldError>
            <Button type="submit" variant="live" size="lg" className="w-full" disabled={submitting}>
              가입 신청
            </Button>
            <Button type="button" variant="outline" size="lg" className="w-full" onClick={() => setStep('account')}>
              이전 — 계정 정보
            </Button>
          </div>
        </form>
      )}
    </AuthCard>
  );
}

/** 일부만 켜졌을 때 가로줄로 보이는 체크박스 — `indeterminate` 는 HTML 속성이 없어 DOM 에 직접 건다 */
function IndeterminateCheckbox({
  id,
  checked,
  indeterminate,
  onChange,
  className,
}: {
  id: string;
  checked: boolean;
  indeterminate: boolean;
  onChange: (next: boolean) => void;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(ev) => onChange(ev.target.checked)}
      className={className}
    />
  );
}

/** 신청서 탭이 링크 인증 여부를 묻는 간격 — 서버의 전용 IP 상한(signup/routes.ts statusLimiter)과 짝이다 */
const EMAIL_LINK_POLL_MS = 3000;

const ROUTES = {
  email: { send: signup.sendEmailCode, check: signup.checkEmailCode, label: '이메일' },
  phone: { send: signup.sendPhoneCode, check: signup.checkPhoneCode, label: '휴대폰' },
} as const;

/**
 * 입력칸 바로 아래 붙는 인증 한 줄.
 * 메일은 링크로도 인증할 수 있어서, 인증번호를 보낸 뒤로는 **탭이 보이는 동안 몇 초마다, 창으로 돌아오는 순간 바로**
 * 서버에 물어본다 — 사용자가 버튼을 누르지 않아도 인증 표시가 바뀐다.
 */
/** 이메일·휴대폰 inline 인증 — 가입과 계정 설정(연락처 변경)이 같이 쓴다 */
export function InlineVerify({
  kind,
  target,
  now,
  verified,
  onVerified,
}: {
  kind: Kind;
  target: string;
  now: number;
  verified: boolean;
  onVerified: (normalized: string) => void;
}) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [resendAt, setResendAt] = useState(0);
  /** 이미 물어본 (대상, 코드) — 확인 요청도 상한을 쓴다 */
  const asked = useRef(new Map<string, boolean>());
  const route = ROUTES[kind];
  const body = (extra: object = {}) => ({ [kind]: target, ...extra }) as never;
  // 부모가 매초(타이머) 새 함수를 넘긴다 — effect 의존성에 두면 매초 다시 등록돼 주기 확인이 한 번도 돌지 않는다
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;
  const linkAlive = expiresAt > now;

  // 대상이 바뀌면 이 칸의 상태를 처음으로 되돌린다
  // biome-ignore lint/correctness/useExhaustiveDependencies: target 이 바뀔 때만 초기화한다
  useEffect(() => {
    setSent(false);
    setCode('');
    setError(null);
    setExpiresAt(0);
  }, [target]);

  /**
   * 링크로 인증했는지 — 탭이 보이는 동안 몇 초마다, 창·탭으로 돌아오는 순간 바로 묻는다.
   * ★ "탭이 다시 보일 때 1회"만 묻던 방식은 메일을 다른 창·휴대폰에서 열면(이 탭은 계속 보이는 상태) 한 번도 묻지 않아
   *   링크 인증이 서버에 기록됐는데도 표시가 안 바뀌었다(실측 260917). 링크가 만료되면(코드와 같은 수명) 멈춘다.
   */
  useEffect(() => {
    if (kind !== 'email' || !sent || verified || !linkAlive) return;
    let stopped = false;
    let inflight = false;
    const ask = async () => {
      if (stopped || inflight || document.visibilityState !== 'visible') return;
      inflight = true;
      try {
        const r = await call(signup.emailVerifyStatus, { body: { email: target } });
        if (r.verified && !stopped) {
          setError(null);
          onVerifiedRef.current(normalizeFor('email', target));
        }
      } catch {
        /* 확인 실패는 조용히 — 다음 차례에 다시 묻고, 인증번호 입력 경로도 살아 있다 */
      } finally {
        inflight = false;
      }
    };
    const onReturn = () => void ask();
    const timer = setInterval(onReturn, EMAIL_LINK_POLL_MS);
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [kind, sent, verified, target, linkAlive]);

  const send = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await call(route.send, { body: body() });
      // 받은 즉시 한 번만 절대시각으로 바꾼다
      setExpiresAt(Date.now() + r.expiresInMs);
      setResendAt(Date.now() + r.resendAfterMs);
      setSent(true);
      setCode('');
      asked.current.clear();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onCode = async (next: string) => {
    const digits = next.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    if (digits.length !== 6) return;
    const key = `${normalizeFor(kind, target)}:${digits}`;
    const cached = asked.current.get(key);
    if (cached !== undefined) {
      setError(cached ? null : '인증번호가 다릅니다');
      if (cached) onVerified(normalizeFor(kind, target));
      return;
    }
    try {
      await call(route.check, { body: body({ code: digits }) });
      asked.current.set(key, true);
      setError(null);
      onVerified(normalizeFor(kind, target));
    } catch (err) {
      if (err instanceof ApiError && err.code.endsWith('code-mismatch')) asked.current.set(key, false);
      setError((err as Error).message);
    }
  };

  if (verified) {
    return (
      <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700 text-xs ring-1 ring-emerald-100">
        <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} />
        {route.label} 인증이 완료됐습니다
      </p>
    );
  }

  const leftSec = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const resendLeft = Math.max(0, Math.ceil((resendAt - now) / 1000));
  return (
    <div className="mt-2 grid gap-1.5">
      {!sent ? (
        <div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-live/30 text-live hover:bg-live/5"
            onClick={() => void send()}
            disabled={busy || !target}
          >
            {route.label} 인증번호 받기
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-10 w-40 rounded-lg border-slate-200 text-center font-semibold tabular-nums tracking-[0.3em] focus:border-live focus:ring-4 focus:ring-live/10"
              inputMode="numeric"
              // 한 화면에 코드칸이 둘이면 OS 가 문자 코드를 메일 칸에도 제안한다
              autoComplete={kind === 'phone' ? 'one-time-code' : 'off'}
              maxLength={6}
              placeholder="인증번호 6자리"
              value={code}
              onChange={(ev) => void onCode(ev.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void send()}
              disabled={busy || resendLeft > 0}
            >
              {resendLeft > 0 ? `재발송 ${resendLeft}초` : '재발송'}
            </Button>
            <span
              className={`rounded-full px-2 py-0.5 font-semibold text-xs tabular-nums ${
                leftSec > 0 ? 'bg-slate-100 text-slate-600' : 'bg-red-50 text-red-600'
              }`}
            >
              {leftSec > 0 ? `남은 시간 ${leftSec}초` : '만료됐습니다 — 다시 받아 주세요'}
            </span>
          </div>
          {kind === 'email' ? (
            <span className="text-slate-500 text-xs">
              메일의 인증번호를 넣거나 메일 속 링크를 누르면 여기서 자동으로 확인됩니다.
            </span>
          ) : null}
        </>
      )}
      <FieldError>{error}</FieldError>
    </div>
  );
}

function DoneStep() {
  const navigate = useNavigate();
  return (
    <AuthCard
      icon={
        <StatusCircle tone="ok">
          <CheckIcon className="h-7 w-7" strokeWidth={3} />
        </StatusCircle>
      }
      title="신청이 접수됐습니다"
      description="신청 내용을 확인한 뒤 담당자가 연락드립니다. 문의사항이 있으면 아래 연락처로 문의해 주세요."
    >
      <Button variant="live" size="lg" className="w-full" onClick={() => void navigate({ to: '/login' })}>
        로그인하러 가기
      </Button>
    </AuthCard>
  );
}
